import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { getUsage, toApiError } from "../services/api";
import type { UsageEntry } from "../types";
import { useAuth } from "./useAuth";

interface RateUsageContextType {
    usage: Record<string, number>;
    entries: UsageEntry[];
    loading: boolean;
    error: string | null;
    refreshUsage: () => Promise<void>;
    getUsageEntry: (featureKey: string) => UsageEntry | undefined;
}

const RateUsageContext = createContext<RateUsageContextType | undefined>(
    undefined,
);

export const RateUsageProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [entries, setEntries] = useState<UsageEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    const refreshUsage = useCallback(async () => {
        if (!user) {
            setEntries([]);
            setError(null);
            return;
        }

        setLoading(true);
        try {
            setEntries(await getUsage());
            setError(null);
        } catch (requestError) {
            setEntries([]);
            setError(
                toApiError(
                    requestError,
                    "Failed to load subscription usage.",
                ).message,
            );
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        void refreshUsage();
    }, [refreshUsage]);

    const usage = useMemo(
        () =>
            Object.fromEntries(
                entries.map((entry) => [entry.featureKey, entry.used]),
            ),
        [entries],
    );

    const getUsageEntry = useCallback(
        (featureKey: string) =>
            entries.find((entry) => entry.featureKey === featureKey),
        [entries],
    );

    const value = useMemo(
        () => ({
            usage,
            entries,
            loading,
            error,
            refreshUsage,
            getUsageEntry,
        }),
        [entries, error, getUsageEntry, loading, refreshUsage, usage],
    );

    return (
        <RateUsageContext.Provider value={value}>
            {children}
        </RateUsageContext.Provider>
    );
};

export const useRateUsageContext = (): RateUsageContextType => {
    const context = useContext(RateUsageContext);
    if (!context) {
        throw new Error(
            "useRateUsageContext must be used within RateUsageProvider",
        );
    }
    return context;
};
